import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLazyQuery, useMutation, useQuery, useApolloClient } from '@apollo/client';
import { signInWithCustomToken } from 'firebase/auth';
import { auth as firebaseAuth, scopeAuthToTab } from '@/lib/firebase';
import { useTrain } from '@/hooks/useTrain';
import RevealText from './partials/RevealText';
import { GameAnswers, GameCard } from './partials/GameCard';
import { QuestionMedia } from './partials/QuestionMedia';
import { useThinkingPhrase } from './thinkingPhrases';
import { GAME_SKIP_REPLY, gameAnswersShown, gameCardView } from './gameCard';
import { useAuth, SIGNED_IN_FOR_INVITE_KEY } from '@/lib/auth-provider';
import {
  CURRENT_USER,
  VALIDATE_INVITATION,
  GET_CATEGORY_ATTACHMENTS,
  GET_SELF_INTERVIEW_CHECK_IN,
} from '@/api/graphql/queries';
import {
  ACCEPT_GLOBAL_LINK_INVITATION,
  ACCEPT_INVITATION,
  SAVE_PERSONAL_DATA,
} from '@/api/graphql/mutations';
import {
  clearLinkKey,
  extractCheckInIdFromPath,
  extractInviteIdFromPath,
  extractLinkKey,
  postMessageToParent,
  storeAuthToken,
} from '@/lib/utils';
import {
  hasCompletedContributorOnboarding,
  hasStartedContributorOnboarding,
} from '@/lib/contributorProfile';
import { initGA, logPageView } from '@/lib/analytics';
import AttachmentModal, { type CategoryAttachment } from '../TrainScreen/partials/AttachmentModal';
import MediaChecklistModal, { type MediaRequest } from '../TrainScreen/partials/MediaChecklistModal';
import LightReplyModal from './partials/LightReplyModal';
import VideoReplyModal from './partials/VideoReplyModal';
import { isVideoCaptureSupported } from '@/lib/videoRecorder';
import TopModal from '../TrainScreen/partials/TopModal';
import ActionsPopover from '../TrainScreen/partials/ActionsPopover';
import { LightGlobalStyles } from './light-ui';
import Loading from './partials/Loading';
import DownloadScreen from './partials/DownloadScreen';
import CheckInUnavailable from './partials/CheckInUnavailable';
import LandingScreen from './partials/LandingScreen';
import WakeOverlay from './partials/WakeOverlay';
import SignInModal from './partials/SignInModal';
import RegistrationModal from './partials/RegistrationModal';
import GlobalLinkConfirmationModal from './partials/GlobalLinkConfirmationModal';
import ConfirmationModal from './partials/ConfirmationModal';
import CreditsInfoModal from './partials/CreditsInfoModal';
import ContributorOnboarding from './partials/ContributorOnboarding';
import PROXI_BRAIN_LOGO from '@/ui/assets/images/proxivideobrainlogo.png';

// ─────────────────────────────────────────────────────────────────────────────
//  Train Screen — Light is now the default and only UI variant.
//  This component mounts the full invitation + auth orchestration from the
//  original TrainScreen (sign-in, registration, global-link confirmation,
//  contributor onboarding, etc.) and renders the in-conversation orb UI
//  natively in the paper/sage editorial language.
// ─────────────────────────────────────────────────────────────────────────────

interface TrainScreenLightProps {
  embedded?: boolean;
}

/** localStorage key for the orb's preferred answer mode ('audio' | 'video'). */
const REPLY_MODE_STORAGE_KEY = 'lp-reply-mode';
/** How an Archive interview names its interviewer. */
const ARCHIVIST_NAME = 'The Archivist';
/** Smallest size the interviewer's message shrinks to before it scrolls instead. */
const QUESTION_MIN_PX = 15;

interface InvitationValidationResponse {
  success: boolean;
  action?:
    | 'accept_invitation'
    | 'accept_anonymously'
    | 'require_login'
    | 'require_registration'
    | 'user_mismatch'
    | 'wrong_user'
    | 'error';
  message?: string;
  statusCode: number;
  currentUser?: {
    id: string;
    email: string | null;
    isAnonymous: boolean;
    isAuthenticated: boolean;
  };
  invitation?: {
    email: string;
    type: 'CIRCLE' | 'GUEST_CONTRIBUTOR' | 'INTERVIEW' | 'GLOBAL_LINK';
    docId: string;
    inviterName: string;
    inviterAvatar?: string | null;
    expiresAt: string;
    categoryId: string;
    expectedUserId?: string;
    circleName?: string;
    /** Title of the ProxiCastSeries this proxicast belongs to, if any. */
    seriesName?: string;
    fullName?: string;
    proxiCastName?: string;
    skipAuth?: boolean;
    leadCaptureMode?: boolean;
    /** An Archive group interview's link (see isArchiveInterview). */
    isArchive?: boolean;
    /** Fun & Games: the game's name when this Archive group interview is a game. */
    game?: string | null;
  };
  document?: { id: string; name: string; description?: string; type: 'circle' | 'proxicast' };
}

export default function TrainScreenLight({ embedded }: TrainScreenLightProps) {
  const [isTopOpen, setIsTopOpen] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showGlobalLinkConfirmation, setShowGlobalLinkConfirmation] = useState(false);
  const [hasHandledGlobalLink, setHasHandledGlobalLink] = useState(false);
  const [isTokenLoading, setIsTokenLoading] = useState(false);
  const [invitationData, setInvitationData] = useState<InvitationValidationResponse | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isExpiredToken, setIsExpiredToken] = useState(false);
  const [hasCalledSignIn, setHasCalledSignIn] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [isAuthenticatedForInterview, setIsAuthenticatedForInterview] = useState(false);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  // How the orb answers: the full-screen video sheet (default) or
  // tap-to-talk audio. Persisted so a contributor who switches to voice
  // gets the orb back in voice mode on their next question/session. Falls
  // back to audio when this device can't capture video at all.
  const [replyMode, setReplyMode] = useState<'audio' | 'video'>(() => {
    if (!isVideoCaptureSupported()) return 'audio';
    try {
      return localStorage.getItem(REPLY_MODE_STORAGE_KEY) === 'audio' ? 'audio' : 'video';
    } catch {
      return 'video';
    }
  });
  const changeReplyMode = (mode: 'audio' | 'video') => {
    setReplyMode(mode);
    try {
      localStorage.setItem(REPLY_MODE_STORAGE_KEY, mode);
    } catch {
      // Private-mode/quota failures just lose the preference, never the tap.
    }
  };
  const [showMediaChecklist, setShowMediaChecklist] = useState(false);
  const [showContributorOnboarding, setShowContributorOnboarding] = useState(false);
  // True once the skipAuth (anonymous-lead) flow has finished the
  // accept-anonymous → signInWithCustomToken → setCategoryId chain. The
  // WakeOverlay button must be gated on this, otherwise a fast-tapping user
  // can fire initTalk before there's a categoryId/auth token bound to the
  // anonymous session and the first getNextMessagesInterview call goes out
  // with no categoryId. In iframes the race is wider because cross-origin
  // accept-anonymous is slower than direct browser navigation.
  const [anonymousAcceptReady, setAnonymousAcceptReady] = useState(false);
  const [pendingSignInResult, setPendingSignInResult] = useState<any>(null);
  const pendingBackgroundWork = useRef<Promise<any> | null>(null);
  const [attachments, setAttachments] = useState<CategoryAttachment[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
  const [hostAvatarFailed, setHostAvatarFailed] = useState(false);
  const [showCreditsInfo, setShowCreditsInfo] = useState(false);

  const token = extractInviteIdFromPath();
  const hasToken = token !== null && token.length > 0;
  const isSelfCreatedInterview =
    hasToken && token && sessionStorage.getItem(`self_created_${token}`) === 'true';
  // Mount-time snapshot: the self_created flag is consumed (removed) by the
  // auth effect right after the invitation validates, so the live read above
  // goes false mid-session. The snapshot keeps the news-tip quick-upload
  // affordance visible for the whole wake screen. (Lost on hard refresh —
  // acceptable; the flag only ever exists on the redirect from the picker.)
  const [isSelfCreatedTip] = useState(isSelfCreatedInterview);
  const [showQuickUploadModal, setShowQuickUploadModal] = useState(false);

  const {
    status,
    isMuted,
    toggleMuted,
    toggleRecording,
    currentMessage,
    messageText,
    playbackProgress,
    bottomSheetOpened,
    showBottomSheet,
    closeBottomSheet,
    submitFormResponse,
    submitVideoResponse,
    isAwake,
    setIsAwake,
    initTalk: originalInitTalk,
    resetChat,
    setCategoryId,
    categoryId,
    getMicLevel,
    isInitializing,
    creditsEarned,
    creditsBalance,
  } = useTrain(undefined);
  const {
    loading,
    currentUser,
    authUser,
    isGuestSigningIn,
    signInGuestContributor,
    signInWithInterviewLink,
    signInWithInviteLink,
    linkInterviewId,
    logout,
  } = useAuth();
  const apolloClient = useApolloClient();

  // Self-interview check-in SMS links (/interview/<si_…>) carry no invitation.
  // Once the owner is signed in, resolve the link to the category that
  // check-in claimed and run the interview on it directly.
  const checkInId = hasToken ? null : extractCheckInIdFromPath();
  const isSignedIn = !!currentUser?._id && !!authUser && !authUser.isAnonymous;
  // A session opened by a texted link only answers interviews: no interview
  // list or account pages for it, so those affordances are hidden.
  const isLinkSession = !!linkInterviewId;

  // An Archive interview texted to someone's phone — a check-in or a group
  // interview invite — carries a key (`?k=…`) that signs them in without a
  // sign-in screen. Read once: it leaves the address bar as soon as it has
  // signed the tab in.
  const linkId = checkInId || (hasToken ? token : null);
  const [linkKey] = useState(() => (linkId ? extractLinkKey() : null));
  const [linkSignIn, setLinkSignIn] = useState<'idle' | 'pending' | 'done' | 'failed' | 'skipped'>('idle');
  // A group interview invitee with no login yet signs up once, with the number
  // the invite was texted to; the key lets that sign-up become theirs.
  const [inviteSignUpPhone, setInviteSignUpPhone] = useState<string | null>(null);
  useEffect(() => {
    if (!linkId || !linkKey || linkSignIn !== 'idle' || loading) return;
    // Someone signed in with their own account keeps it, and its browser-wide
    // login, rather than trading it for a tab-scoped link session; so does a
    // tab already holding this link's session. Another link's session is
    // replaced, since it only answers its own interviews.
    const hasOwnAccount = !!authUser && !authUser.isAnonymous && !isLinkSession;
    if (hasOwnAccount || (isLinkSession && linkInterviewId === linkId)) {
      clearLinkKey();
      setLinkSignIn('skipped');
      return;
    }
    setLinkSignIn('pending');
    if (checkInId) {
      signInWithInterviewLink(checkInId, linkKey).then(ok => {
        if (ok) clearLinkKey();
        // Refused (forged, or the text is too old): ask them to sign in, as before.
        setLinkSignIn(ok ? 'done' : 'failed');
      });
      return;
    }
    signInWithInviteLink(linkId, linkKey).then(result => {
      if (result.status === 'signed-in') clearLinkKey();
      if (result.status === 'needs-registration') setInviteSignUpPhone(result.phone ?? '');
      // Otherwise the invite is validated as usual, which asks them to sign in.
      setLinkSignIn(result.status === 'signed-in' ? 'done' : 'failed');
    });
  }, [linkId, linkKey, linkSignIn, loading, authUser, isLinkSession, linkInterviewId]);
  const isLinkSigningIn =
    !!linkKey && (linkSignIn === 'idle' || linkSignIn === 'pending') && !isSignedIn;
  const { data: checkInData, error: checkInQueryError } = useQuery(GET_SELF_INTERVIEW_CHECK_IN, {
    variables: { interviewId: checkInId },
    skip: !checkInId || !isSignedIn,
    fetchPolicy: 'network-only',
  });
  const checkInResult = checkInData?.getSelfInterviewCheckIn;
  const checkIn = checkInResult?.success ? checkInResult : null;
  const checkInError =
    checkInQueryError?.message || (checkInResult && !checkInResult.success ? checkInResult.error : null);
  // Shown on the start card the way a show's series name is, e.g.
  // "My life · Early Beginnings" — matching the check-in text.
  const checkInLabel = checkIn
    ? [checkIn.selfInterviewName, checkIn.topicName].filter(Boolean).join(' · ')
    : undefined;

  useEffect(() => {
    if (checkIn?.categoryId) setCategoryId(checkIn.categoryId);
  }, [checkIn?.categoryId]);

  // The Archive sets its members up in its own onboarding, so the Proxi
  // contributor onboarding (channel, consent, profile) never opens on an
  // Archive group interview link or an Archive self-interview check-in.
  const isArchiveInterview = !!invitationData?.invitation?.isArchive || !!checkIn?.isArchive;
  // The Archive's interviewer, whoever started the interview. The API names it
  // on group interview links too; this covers check-ins, which carry no host.
  const interviewerName = isArchiveInterview
    ? ARCHIVIST_NAME
    : invitationData?.invitation?.inviterName || (checkIn ? 'Proxi' : 'Admin');

  const [acceptInvitation] = useMutation(ACCEPT_INVITATION);
  const [acceptGlobalLinkInvitation] = useMutation(ACCEPT_GLOBAL_LINK_INVITATION);
  const [savePersonalData] = useMutation(SAVE_PERSONAL_DATA);

  // Per-show media-upload checklist (from the validated invitation). When
  // present the interview surfaces "Complete Media Upload Requests" instead of
  // the single photo prompt; when empty, the original behaviour is unchanged.
  const mediaRequests: MediaRequest[] =
    (invitationData?.invitation as any)?.mediaRequests ?? [];
  const checklistActive = mediaRequests.length > 0;

  // Seed already-uploaded attachments so checklist items show as done on reload.
  useQuery(GET_CATEGORY_ATTACHMENTS, {
    variables: { categoryId },
    skip: !categoryId || !checklistActive,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.getCategoryAttachments) setAttachments(data.getCategoryAttachments);
    },
  });

  const currentLanguage = selectedLanguage || currentUser?.personalData?.language || 'ENG';

  const handleLanguageChange = async (languageCode: string) => {
    setSelectedLanguage(languageCode);
    try {
      await savePersonalData({
        variables: { fields: { language: languageCode } },
        update: (cache, { data }) => {
          if (data?.savePersonalData?.user) {
            cache.writeQuery({ query: CURRENT_USER, data: { me: data.savePersonalData.user } });
          }
        },
      });
    } catch (err) {
      console.error('Error updating language:', err);
      setSelectedLanguage(null);
    }
  };

  const [validateInvitation, { loading: validationLoading }] = useLazyQuery(VALIDATE_INVITATION, {
    onCompleted: (data) => {
      if (data?.validateInvitation?.success) {
        const { action } = data.validateInvitation;
        if (action === 'user_mismatch' || action === 'wrong_user') {
          setValidationError(
            data.validateInvitation.message ||
              'This invitation is for a different user. Please sign in with the correct account.',
          );
          const hasUserIdMismatch =
            data.validateInvitation.invitation?.expectedUserId &&
            data.validateInvitation.currentUser?.id &&
            data.validateInvitation.currentUser.id !== data.validateInvitation.invitation.expectedUserId;
          const isAnonymousUser = data.validateInvitation.currentUser?.isAnonymous;
          setInvitationData(data.validateInvitation);
          setIsTokenLoading(false);
          if (hasUserIdMismatch && data.validateInvitation.currentUser?.isAuthenticated && !isAnonymousUser) {
            logout().then(() => {
              if (token) {
                setTimeout(() => {
                  validateInvitation({ variables: { token } });
                }, 300);
              }
            });
          }
          return;
        }
        setInvitationData(data.validateInvitation);
        setValidationError(null);
        setIsExpiredToken(false);
        setIsTokenLoading(false);
        setHasCalledSignIn(false);
        postMessageToParent('train-link:ready');
      } else {
        const errorMessage = data?.validateInvitation?.message || 'Invalid invitation token';
        setValidationError(errorMessage);
        const isExpired =
          errorMessage.toLowerCase().includes('expired') ||
          errorMessage.toLowerCase().includes('not found') ||
          data?.validateInvitation?.statusCode === 404;
        setIsExpiredToken(isExpired);
        setIsTokenLoading(false);
        postMessageToParent('train-link:error', { message: errorMessage });
      }
    },
    onError: (err) => {
      setValidationError(err.message);
      setIsExpiredToken(true);
      setIsTokenLoading(false);
      postMessageToParent('train-link:error', { message: err.message });
    },
  });

  const isAuthenticatedGuestContributor = (data: any) => {
    if (data?.action === 'accept_invitation') return false;
    if (data?.action === 'require_login') return true;
    return false;
  };

  useEffect(() => {
    if (invitationData?.success && invitationData?.invitation?.type === 'GUEST_CONTRIBUTOR') {
      if (invitationData.action === 'user_mismatch' || invitationData.action === 'wrong_user') {
        setShowSignInModal(true);
        return;
      }
      const needsAuth = isAuthenticatedGuestContributor(invitationData);
      if (needsAuth) {
        if (currentUser && currentUser.personalData?.email) {
          const userEmail = currentUser.personalData.email.toLowerCase();
          const invitationEmail = invitationData.invitation.email?.toLowerCase();
          const emailMatch = !invitationEmail || userEmail === invitationEmail;
          const idMatch = invitationData.invitation.expectedUserId
            ? currentUser._id === invitationData.invitation.expectedUserId
            : true;
          if (!emailMatch || !idMatch) {
            const isAnonymous = authUser?.isAnonymous || false;
            if (!idMatch && invitationData.invitation.expectedUserId && !isAnonymous) {
              logout().then(() => setShowSignInModal(true));
            } else {
              setShowSignInModal(true);
            }
          }
        } else {
          setShowSignInModal(true);
        }
      } else {
        // Signed in with their own login as the invitee (a guest invite that
        // went to someone's existing account): keep it. Signing in as an
        // anonymous guest here would move their account to a throwaway login.
        if (
          currentUser &&
          authUser &&
          !authUser.isAnonymous &&
          invitationData.invitation.expectedUserId &&
          currentUser._id === invitationData.invitation.expectedUserId
        ) {
          return;
        }
        if (currentUser) {
          const userEmail = currentUser.personalData?.email?.toLowerCase();
          const invitationEmail = invitationData.invitation.email?.toLowerCase();
          const isAnonymous = authUser?.isAnonymous || false;
          const emailMismatch = userEmail && invitationEmail && userEmail !== invitationEmail;
          const userIdMismatch =
            invitationData.invitation.expectedUserId &&
            currentUser._id !== invitationData.invitation.expectedUserId;
          // Wrong user is currently authenticated for this invitation. Sign
          // them out AND immediately re-sign in fresh for this invitation —
          // chaining the new sign-in directly off logout().then() instead of
          // just resetting hasCalledSignIn. Relying on the effect to re-run
          // and pick up the change deadlocks: useQuery(CURRENT_USER) doesn't
          // auto-refetch on Firebase auth changes, so `currentUser` stays
          // stuck on the previous invitation's user even after sign-out, the
          // mismatch keeps re-detecting, and the explicit signInGuestContributor
          // call below never fires — leaving the page with stale display
          // state and no auth token, so the next getNextMessages errors with
          // "You have to provide user".
          if ((emailMismatch || userIdMismatch) && !isAnonymous) {
            if (!isGuestSigningIn && !hasCalledSignIn && token) {
              setHasCalledSignIn(true);
              logout().then(() => signInGuestContributor(invitationData, token));
            }
            return;
          }
          if (isAnonymous && userIdMismatch) {
            if (!isGuestSigningIn && !hasCalledSignIn && token) {
              setHasCalledSignIn(true);
              logout().then(() => signInGuestContributor(invitationData, token));
            }
            return;
          }
        }
        if (!isGuestSigningIn && !hasCalledSignIn && token) {
          setHasCalledSignIn(true);
          signInGuestContributor(invitationData, token);
        }
      }
    }
  }, [
    invitationData?.success,
    invitationData?.invitation?.type,
    invitationData?.invitation?.expectedUserId,
    currentUser,
    isGuestSigningIn,
    token,
    hasCalledSignIn,
  ]);

  useEffect(() => {
    if (invitationData?.success && invitationData?.invitation?.type === 'INTERVIEW') {
      if (loading) return;
      if (isSelfCreatedInterview) {
        setIsAuthenticatedForInterview(true);
        setShowSignInModal(false);
        sessionStorage.removeItem(`self_created_${token}`);
        return;
      }
      if (invitationData.action === 'user_mismatch' || invitationData.action === 'wrong_user') {
        setIsAuthenticatedForInterview(false);
        setShowSignInModal(true);
        return;
      }
      if (invitationData.action === 'require_login') {
        if (currentUser && currentUser.personalData?.email) {
          const userEmail = currentUser.personalData.email.toLowerCase();
          const invitationEmail = invitationData.invitation?.email?.toLowerCase();
          const expectedUserId = invitationData.invitation?.expectedUserId;
          const emailMatch = !invitationEmail || userEmail === invitationEmail;
          const idMatch = expectedUserId ? currentUser._id === expectedUserId : true;
          if (emailMatch && idMatch) {
            setIsAuthenticatedForInterview(true);
            setShowSignInModal(false);
            return;
          }
        }
        setIsAuthenticatedForInterview(false);
        setShowSignInModal(true);
        return;
      }
      if (
        invitationData.action === 'accept_invitation' &&
        invitationData.currentUser?.isAuthenticated
      ) {
        setIsAuthenticatedForInterview(true);
        setShowSignInModal(false);
        return;
      }
      if (currentUser && currentUser.personalData?.email) {
        const userEmail = currentUser.personalData.email.toLowerCase();
        const invitationEmail = invitationData.invitation?.email?.toLowerCase();
        const emailMatch = !invitationEmail || userEmail === invitationEmail;
        const idMatch = invitationData.invitation?.expectedUserId
          ? currentUser._id === invitationData.invitation.expectedUserId
          : true;
        if (emailMatch && idMatch) {
          setIsAuthenticatedForInterview(true);
          setShowSignInModal(false);
        } else {
          if (invitationEmail) {
            setValidationError(
              `This invitation is for ${invitationData.invitation?.email}. Please sign in with the correct account.`,
            );
          }
          const isAnonymous = authUser?.isAnonymous || false;
          if (!idMatch && invitationData.invitation?.expectedUserId && !isAnonymous) {
            logout().then(() => {
              setIsAuthenticatedForInterview(false);
              setShowSignInModal(true);
            });
          } else {
            setIsAuthenticatedForInterview(false);
            setShowSignInModal(true);
          }
        }
      } else {
        setIsAuthenticatedForInterview(false);
        setShowSignInModal(true);
      }
    }
  }, [invitationData, currentUser, loading]);

  useEffect(() => {
    if (invitationData?.success && invitationData?.invitation?.type === 'GLOBAL_LINK') {
      if (isAwake || hasHandledGlobalLink) return;
      if (loading) return;
      setHasHandledGlobalLink(true);

      // Anonymous-lead path: the parent ProxiCast opted into skipAuth + the
      // backend already returned `accept_anonymously`. Spin up an anonymous
      // Firebase user + interview via REST, then drop straight into the chat
      // — no sign-in modal, no confirmation modal, no onboarding. We rely on
      // post-interview transcript extraction to capture lead identity.
      if (
        invitationData?.action === 'accept_anonymously' ||
        invitationData?.invitation?.skipAuth
      ) {
        setShowGlobalLinkConfirmation(false);
        setShowRegistration(false);
        setShowSignInModal(false);
        (async () => {
          try {
            const apiBase = import.meta.env.VITE_GRAPHQL_HOST || '';
            const referrer = typeof document !== 'undefined' ? document.referrer : undefined;
            const urlParams = new URLSearchParams(window.location.search);
            const res = await fetch(`${apiBase}/api/invitations/${token}/accept-anonymous`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                referrer,
                utmSource: urlParams.get('utm_source') || undefined,
                utmMedium: urlParams.get('utm_medium') || undefined,
                utmCampaign: urlParams.get('utm_campaign') || undefined,
              }),
            });
            if (!res.ok) throw new Error(`accept-anonymous failed: ${res.status}`);
            const payload = await res.json();
            if (!payload.success || !payload.firebaseCustomToken) {
              throw new Error(payload.message || 'accept-anonymous returned no token');
            }
            // Bind invite token in sessionStorage before Firebase fires onIdTokenChanged,
            // mirroring the signInGuestContributor pattern.
            if (token) sessionStorage.setItem(SIGNED_IN_FOR_INVITE_KEY, token);
            // Apollo's getClient() fires `auth.signOut()` fire-and-forget when
            // the invite changes between mounts. If that Promise resolves
            // AFTER our signInWithCustomToken (race), it signs out the freshly
            // minted anonymous user — the next mutation then goes out with
            // no Firebase ID token and the resolver rejects with
            // "You have to provide user". Await an explicit signOut here so
            // the previous anon session is fully torn down before we sign in
            // the new one.
            if (firebaseAuth.currentUser?.isAnonymous) {
              try {
                await firebaseAuth.signOut();
              } catch {
                /* non-fatal — signInWithCustomToken below will replace state */
              }
            }
            // Scope this per-invitation guest session to the tab so other
            // invitation links each get their own user (see firebase.ts).
            await scopeAuthToTab();
            const cred = await signInWithCustomToken(
              firebaseAuth,
              payload.firebaseCustomToken,
            );
            // Synchronously populate the auth token cache. Apollo's authLink
            // reads from localStorage at mutation time, and `onIdTokenChanged`
            // (which normally writes the token) fires asynchronously — so the
            // first mutation after sign-in would otherwise race and go out
            // unauthenticated. Fetch + store the ID token ourselves before
            // we continue.
            try {
              const idToken = await cred.user.getIdToken();
              if (idToken) storeAuthToken(idToken);
            } catch (tokenErr) {
              console.warn(
                '[TrainScreenLight] failed to fetch id token after anonymous accept:',
                tokenErr,
              );
            }
            if (payload.categoryId) {
              setCategoryId(payload.categoryId);
            }
            // Mark the skipAuth chain done so the WakeOverlay button can enable.
            // Until this fires, tapping Start would race accept-anonymous and
            // dispatch the first call with no categoryId.
            setAnonymousAcceptReady(true);
          } catch (e: any) {
            console.warn('[TrainScreenLight] anonymous accept failed:', e?.message || e);
            setValidationError(e?.message || 'Failed to start the interview');
          }
        })();
        return;
      }

      if (currentUser && currentUser.personalData?.email && !authUser?.isAnonymous) {
        setShowGlobalLinkConfirmation(true);
        setShowRegistration(false);
        setShowSignInModal(false);
      } else {
        setShowSignInModal(true);
        setShowGlobalLinkConfirmation(false);
        setShowRegistration(false);
      }
    }
  }, [invitationData, currentUser, authUser, isAwake, hasHandledGlobalLink, loading, token, setCategoryId]);

  useEffect(() => {
    if (isAwake) postMessageToParent('train-link:started');
  }, [isAwake]);

  useEffect(() => {
    if (invitationData?.success && invitationData?.invitation?.categoryId) {
      setCategoryId(invitationData.invitation.categoryId);
    }
  }, [invitationData?.success, invitationData?.invitation?.categoryId]);

  useEffect(() => {
    if (currentMessage?.userResponseType === 'END_CHAT') {
      // The end-of-interview confirmation modal is intentionally disabled:
      // the main stage already shows the closing message, the "+N" credits
      // chip in the top bar, and the "Interview complete" orb — leave the
      // user there instead of covering it.
      // setShowConfirmation(true);
      postMessageToParent('train-link:completed');
    }
  }, [currentMessage?.userResponseType]);

  const acceptGuestInvitation = async () => {
    if (
      !token ||
      (invitationData?.invitation?.type !== 'GUEST_CONTRIBUTOR' &&
        invitationData?.invitation?.type !== 'INTERVIEW')
    ) {
      return;
    }
    try {
      await acceptInvitation({ variables: { token } });
    } catch {
      // non-fatal
    }
  };

  const initTalk = async () => {
    // Anonymous lead-capture global links skip onboarding + signin entirely —
    // the visitor is on the marketing CTA path and the whole point is zero
    // friction. We rely on transcript-based lead extraction post-interview.
    const isAnonymousLeadFlow = !!invitationData?.invitation?.skipAuth;

    // Only a genuinely blank account (no channel/consent AND no profile
    // basics) is walked through onboarding on the way into an interview.
    // Partial progress means they've been through this before — never
    // re-show it here.
    if (
      !isAnonymousLeadFlow &&
      !isArchiveInterview &&
      currentUser &&
      currentUser.personalData?.email &&
      !hasStartedContributorOnboarding(currentUser)
    ) {
      setShowContributorOnboarding(true);
      return;
    }

    // Self-heal: the user's profile already carries everything onboarding
    // collects (channel/consent + name + occupation) but no finished flag —
    // e.g. it was captured by an SMS opt-in or a Studio import. Stamp the
    // flag in the background so every future surface short-circuits on it.
    if (
      !isAnonymousLeadFlow &&
      currentUser &&
      !currentUser.serviceData?.contributorOnboardingFinished &&
      !currentUser.serviceData?.onboardingFinished &&
      hasCompletedContributorOnboarding(currentUser)
    ) {
      savePersonalData({
        variables: { fields: { contributorOnboardingFinished: true } },
      }).catch(() => {
        /* non-fatal — the data-driven check covers the next visit too */
      });
    }

    const needsAuth =
      !isAnonymousLeadFlow &&
      (invitationData?.invitation?.type === 'INTERVIEW' ||
        (invitationData?.invitation?.type === 'GUEST_CONTRIBUTOR' &&
          isAuthenticatedGuestContributor(invitationData)));

    if (needsAuth && !isAuthenticatedForInterview && !currentUser?.personalData?.email) {
      setShowSignInModal(true);
      return;
    }

    if (needsAuth && currentUser?.personalData?.email && invitationData?.invitation?.email) {
      const userEmail = currentUser.personalData.email.toLowerCase();
      const invitationEmail = invitationData.invitation.email.toLowerCase();
      if (userEmail !== invitationEmail) {
        setShowSignInModal(true);
        return;
      }
    }

    const categoryIdFromInvite = invitationData?.invitation?.categoryId || checkIn?.categoryId;
    if (categoryIdFromInvite) setCategoryId(categoryIdFromInvite);

    try {
      await originalInitTalk(categoryIdFromInvite);
      if (
        invitationData?.invitation?.type === 'GUEST_CONTRIBUTOR' ||
        invitationData?.invitation?.type === 'INTERVIEW'
      ) {
        await acceptGuestInvitation();
      }
    } catch {
      // non-fatal
    }
  };

  useEffect(() => {
    initGA();
    logPageView();
  }, []);

  // Tracks whether we've already auto-fired initTalk for the embedded modal
  // flow. Declared up here so the effect below the `isStartDisabled` const can
  // share it without re-creating across renders.
  const hasAutoWakedRef = useRef(false);

  useEffect(() => {
    // A texted invite's key is traded for a session first, so the invite is
    // checked as the invitee and opens straight into the interview.
    if (hasToken && token && !loading && !isLinkSigningIn) {
      setIsTokenLoading(true);
      validateInvitation({ variables: { token } });
    }
  }, [hasToken, token, loading, isLinkSigningIn, validateInvitation]);

  useEffect(() => {
    if (
      hasToken &&
      token &&
      authUser &&
      !authUser.isAnonymous &&
      invitationData?.action === 'require_login'
    ) {
      validateInvitation({ variables: { token } });
    }
  }, [authUser, hasToken, token, invitationData?.action]);

  useEffect(() => {
    if (
      hasToken &&
      token &&
      currentUser?._id &&
      invitationData?.invitation?.type === 'INTERVIEW'
    ) {
      const userEmail = currentUser.personalData?.email?.toLowerCase();
      const invitationEmail = invitationData.invitation?.email?.toLowerCase();
      const expectedUserId = invitationData.invitation?.expectedUserId;
      const emailMatch = !invitationEmail || userEmail === invitationEmail;
      const idMatch = expectedUserId ? currentUser._id === expectedUserId : true;
      // For anonymous-friendly invitations (no email/expectedUserId), the
      // match is vacuously true for any signed-in user. Don't auto-approve
      // a stale anonymous Firebase user — the main INTERVIEW effect will
      // trigger a fresh signInGuestContributor for this invitation.
      const hasConstraint = !!invitationEmail || !!expectedUserId;
      if (emailMatch && idMatch && (hasConstraint || !authUser?.isAnonymous)) {
        setIsAuthenticatedForInterview(true);
        setShowSignInModal(false);
      } else if (!(emailMatch && idMatch)) {
        validateInvitation({ variables: { token } });
      }
    }
  }, [currentUser?._id]);

  useEffect(() => {
    if (
      currentUser &&
      currentUser.personalData?.firstName &&
      currentUser.personalData?.lastName &&
      currentUser.personalData?.email
    ) {
      setShowRegistration(false);
    } else {
      if (
        // A check-in's owner already has an account (they signed in to get here).
        checkIn ||
        invitationData?.invitation?.type === 'GUEST_CONTRIBUTOR' ||
        invitationData?.invitation?.type === 'INTERVIEW' ||
        invitationData?.invitation?.type === 'GLOBAL_LINK'
      ) {
        setShowRegistration(false);
      } else {
        setShowRegistration(true);
      }
    }
  }, [
    currentUser?.personalData?.firstName,
    currentUser?.personalData?.lastName,
    currentUser?.personalData?.email,
    invitationData?.invitation?.type,
    checkIn?.categoryId,
  ]);

  useEffect(() => {
    document.body.classList.add('lp-active');
    return () => document.body.classList.remove('lp-active');
  }, []);

  // Gate Start on auth being bound to the invitation's intended user.
  // Without the expectedUserId check, the logout()→signInGuestContributor
  // dance leaves a small window where `isGuestSigningIn` is still false
  // (logout is mid-flight) but the previous user's auth token is still in
  // localStorage. Clicking Start in that window fires getNextMessagesInterview
  // with the old token and loads the previous user's chat — the wake screen
  // shows the new contributor's name (sourced from invitationData, not auth),
  // so it looks correct but the request resolves to the wrong user.
  const expectedUserId = invitationData?.invitation?.expectedUserId;
  // Fall back to the id the server already returned in `validateInvitation`
  // when the local CURRENT_USER query hasn't populated yet — otherwise a
  // transient failure on that parallel call deadlocks Start with a perpetual
  // spinner, even though the server has already confirmed the right user is
  // authenticated for this invitation.
  const localUserId = currentUser?._id ?? invitationData?.currentUser?.id;
  const userBoundToInvite = !expectedUserId || localUserId === expectedUserId;
  // skipAuth (anonymous-lead) global links bind the visitor to a fresh
  // categoryId server-side via accept-anonymous. Until that completes,
  // there's no categoryId/auth token to send with getNextMessagesInterview,
  // so the first call would go out empty and the backend would fail to
  // resolve a category — which is what made "Start interview" silently
  // no-op in the iframe.
  const isSkipAuthFlow = !!invitationData?.invitation?.skipAuth;
  const isStartDisabled =
    isGuestSigningIn ||
    !userBoundToInvite ||
    (invitationData?.invitation?.type === 'INTERVIEW' && !isAuthenticatedForInterview) ||
    (isSkipAuthFlow && !anonymousAcceptReady);

  // GLOBAL_LINK invitations that aren't the zero-friction anonymous/skipAuth
  // lead flow must be started from the confirmation or sign-in modal (whose
  // click is also the audio-unlock gesture) — never auto-waked. Auto-waking
  // them races that modal: on the render where invitationData first lands, the
  // effect that *shows* the confirmation modal has only scheduled its state, so
  // `showGlobalLinkConfirmation` is still false inside the auto-wake effect and
  // its stale guard lets it fire a second, category-less initTalk underneath
  // the modal. That start resumes the user's previous interview and plays it
  // over the new one — the "both interviews at once" overlap.
  const isGlobalLinkNeedingModal =
    invitationData?.invitation?.type === 'GLOBAL_LINK' &&
    invitationData?.action !== 'accept_anonymously' &&
    !invitationData?.invitation?.skipAuth;

  // When the page is loaded inside the InterviewTrainModal iframe in Proxi
  // Studio, the user has already tapped "Try a Conversation" (or equivalent) in
  // the parent window — making them tap "Start interview" again inside the
  // iframe reads as a dead step. The iframe is granted `autoplay` permission,
  // so once the invitation is validated and the wake screen would otherwise
  // sit there waiting for input we trigger initTalk ourselves. Guarded by a
  // ref so this only fires once per mount; sign-in/onboarding/registration
  // surfaces still take priority because initTalk re-checks those conditions
  // and bails to the appropriate modal if anything's missing.
  useEffect(() => {
    if (!embedded) return;
    if (hasAutoWakedRef.current) return;
    if (isAwake) return;
    // Global links that need the confirmation/sign-in modal start from that
    // modal's click, not from auto-wake — see isGlobalLinkNeedingModal.
    if (isGlobalLinkNeedingModal) return;
    if (loading || isStartDisabled) return;
    if (!invitationData?.success) return;
    if (showSignInModal) return;
    if (showRegistration) return;
    if (showContributorOnboarding) return;
    if (showGlobalLinkConfirmation) return;
    if (showConfirmation) return;
    hasAutoWakedRef.current = true;
    void initTalk();
  }, [
    embedded,
    isAwake,
    isGlobalLinkNeedingModal,
    loading,
    isStartDisabled,
    invitationData?.success,
    showSignInModal,
    showRegistration,
    showContributorOnboarding,
    showGlobalLinkConfirmation,
    showConfirmation,
  ]);

  const isUserTurn = status === 'USER_RECORDING';
  const isRoxyActive = status === 'ROXY_TYPING' || status === 'ROXY_SPEAKING';
  const disabled = status === 'LOADING' || status === 'ROXY_TYPING' || status === 'ROXY_SPEAKING';
  const isEndChat = currentMessage?.userResponseType === 'END_CHAT';
  // Backstory: the family photo (or clip, recording, document) a question is
  // about, shown above it. It stays up through the confirmation and while the
  // next question is prepared; the goodbye has none.
  const questionMedia = !isEndChat ? (currentMessage?.media ?? null) : null;
  const hasQuestionMedia = !!questionMedia;
  // NONE-type messages (transitional intros that auto-advance) still need their
  // text visible while Roxy speaks them. Only suppress NONE text once playback
  // has ended and the screen is idle, so stale message text doesn't linger.
  const showText =
    !!messageText &&
    (status === 'ROXY_SPEAKING' ||
      status === 'ROXY_TYPING' ||
      currentMessage?.userResponseType !== 'NONE');
  const actionsDisabled = disabled || bottomSheetOpened;
  // Rotates while the interviewer prepares the next question so a long wait
  // doesn't sit on one word.
  const thinkingPhrase = useThinkingPhrase(status === 'ROXY_TYPING');

  // Fun & Games: a message that is part of a game carries `game`. The game's
  // card takes the host avatar's place (it stays up between messages, as the
  // avatar does), and the ways to answer sit under the message and ride on the
  // video sheet. Answers are still ordinary turns, so a message without `game`
  // changes nothing here.
  const currentGame = currentMessage?.game ?? null;
  const gameCard = useMemo(() => gameCardView(currentGame), [currentGame]);
  const showGameCard = !!gameCard && isAwake;
  const showGameAnswers =
    !!gameCard && isAwake && showText && gameAnswersShown(gameCard, isEndChat);

  // Design size by length. The message is then fitted to its row (see
  // RevealText): it only ever shrinks from here, down to QUESTION_MIN_PX.
  const questionFontSize = useMemo(() => {
    const len = messageText?.length ?? 0;
    if (len > 220) return 'clamp(18px, 2.4vw, 30px)';
    if (len > 140) return 'clamp(22px, 2.9vw, 36px)';
    return 'clamp(26px, 3.4vw, 44px)';
  }, [messageText]);

  const showPhotoHint =
    !questionMedia &&
    showText &&
    isAwake &&
    !isEndChat &&
    // A game has its own photo button, for when it wants one.
    !showGameCard &&
    !!categoryId &&
    (checklistActive || !currentUser?.personalData?.avatar);

  // Height the message may take: its grid row, less the photo hint or a game's
  // answers below it.
  // The row's size never depends on the text (it stretches to its minmax(0, 1fr)
  // track), so fitting the text can't feed back into this measurement.
  // Held in state (a callback ref) so the measurement starts whenever the row
  // mounts, including after the loading and landing screens.
  const [questionRow, setQuestionRow] = useState<HTMLDivElement | null>(null);
  const [questionRoom, setQuestionRoom] = useState<number | null>(null);
  useLayoutEffect(() => {
    const row = questionRow;
    if (!row) return;
    const measure = () => {
      const gap = parseFloat(getComputedStyle(row).rowGap) || 0;
      const others = Array.from(row.children).filter(
        el => !(el as HTMLElement).dataset.questionBox,
      ) as HTMLElement[];
      const taken = others.reduce((sum, el) => sum + el.offsetHeight + gap, 0);
      const room = Math.max(0, row.clientHeight - taken);
      setQuestionRoom(prev => (prev != null && Math.abs(prev - room) < 0.5 ? prev : room));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(row);
    Array.from(row.children).forEach(el => ro.observe(el));
    return () => ro.disconnect();
  }, [questionRow, showPhotoHint, showGameAnswers, hasQuestionMedia]);

  // If the user taps any control before the interview has actually started
  // (WakeOverlay was somehow bypassed or its tap didn't fire), route through
  // initTalk so this tap becomes the wake gesture instead of submitting a
  // stray "pass" reply to a chat that doesn't exist yet. Otherwise the
  // backend treats the first-tap reply as a first-message call and the
  // session summary plays only after the user "skips" something that isn't
  // there.
  const wakeIfNeeded = async (): Promise<boolean> => {
    if (isAwake) return false;
    await initTalk();
    return true;
  };

  const handleSkip = async () => {
    if (isRoxyActive || actionsDisabled || isEndChat) return;
    if (await wakeIfNeeded()) return;
    // A game skips its round on "skip"; everything else passes on "pass".
    await submitFormResponse({ text: currentGame ? GAME_SKIP_REPLY : 'pass' });
  };
  const handleTextReply = async () => {
    if (actionsDisabled || isEndChat) return;
    if (await wakeIfNeeded()) return;
    showBottomSheet();
  };
  const handleOpenAttachments = async () => {
    if (actionsDisabled || isEndChat) return;
    if (await wakeIfNeeded()) return;
    setShowAttachmentModal(true);
  };
  // A game's choice and action buttons answer exactly as if their text had
  // been typed and sent.
  const handleGameReply = async (text: string) => {
    if (actionsDisabled || isUserTurn || isEndChat) return;
    if (await wakeIfNeeded()) return;
    await submitFormResponse({ text });
  };
  const videoSupported = useMemo(() => isVideoCaptureSupported(), []);

  // The video sheet stays open between answers (it shows each next question
  // as an overlay on the live camera) — but when the interviewer wraps up,
  // close it so the completion screen underneath is visible while the
  // goodbye plays.
  useEffect(() => {
    if (showVideoModal && isEndChat) setShowVideoModal(false);
  }, [showVideoModal, isEndChat]);

  // ─── Render gates ─────────────────────────────────────────────────────────
  if (!hasToken) {
    if (checkInId && isLinkSigningIn) {
      return (
        <>
          <LightGlobalStyles />
          <Loading />
        </>
      );
    }
    if (!checkInId || !isSignedIn) {
      return (
        <>
          <LightGlobalStyles />
          <LandingScreen />
        </>
      );
    }
    if (checkInError) {
      return (
        <>
          <LightGlobalStyles />
          <CheckInUnavailable message={checkInError} onSignOut={() => void logout()} />
        </>
      );
    }
    if (!checkIn) {
      return (
        <>
          <LightGlobalStyles />
          <Loading />
        </>
      );
    }
    // Resolved check-in: fall through to the interview screen.
  }

  if (hasToken && (isTokenLoading || validationLoading || isLinkSigningIn)) {
    return (
      <>
        <LightGlobalStyles />
        <Loading />
      </>
    );
  }

  if (
    hasToken &&
    validationError &&
    invitationData?.action !== 'user_mismatch' &&
    invitationData?.action !== 'wrong_user'
  ) {
    return (
      <>
        <LightGlobalStyles />
        <DownloadScreen isExpiredToken={isExpiredToken} errorMessage={validationError} />
      </>
    );
  }

  if (hasToken && !invitationData) {
    return (
      <>
        <LightGlobalStyles />
        <Loading />
      </>
    );
  }

  return (
    <>
      <LightGlobalStyles />
      <div className="lp-root">
        <div className="lp-grain" aria-hidden />
        <div className="lp-rings" aria-hidden />

        <main
          style={{
            position: 'relative',
            zIndex: 1,
            height: '100%',
            display: 'grid',
            // minmax(0, 1fr) lets the question row shrink instead of growing
            // past its share for long prompts — without it, content pushes
            // the orb (and the action bar below it) off the bottom of the
            // fixed, overflow:hidden lp-root viewport. The dedicated host-
            // avatar row keeps its vertical position stable as questions of
            // different lengths cycle through.
            gridTemplateRows: 'auto auto minmax(0, 1fr) auto auto',
            // Pin the single column to the available width. Without this the
            // column tracks the widest child's natural width (the action bar
            // on phones), which silently widens <main> past the viewport and
            // shifts the whole stage off-center to the right.
            gridTemplateColumns: 'minmax(0, 1fr)',
            alignItems: 'center',
            justifyItems: 'center',
            padding: 'clamp(20px, 5vh, 56px) clamp(20px, 6vw, 80px)',
            gap: 'clamp(20px, 3.6vh, 40px)',
            maxWidth: 1100,
            margin: '0 auto',
          }}
        >
          {/* Top bar */}
          <header style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ width: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
              <span className="lp-frost-chip lp-topbar-chip">
                <span
                  style={{
                    width: 7, height: 7, borderRadius: 99, flex: 'none',
                    background: 'var(--lp-pink)',
                    boxShadow: '0 0 12px var(--lp-pink)',
                    animation: 'lp-pulse 2.2s ease-in-out infinite',
                  }}
                />
                {isArchiveInterview ? ARCHIVIST_NAME : 'Proxi Interviewer'}
              </span>
              {creditsBalance != null && (
                <button
                  type="button"
                  onClick={() => setShowCreditsInfo(true)}
                  className="lp-frost-chip lp-topbar-chip lp-credits-chip"
                  style={{ cursor: 'pointer' }}
                  aria-label={isArchiveInterview ? 'What are credits?' : 'What are Proxi Credits?'}
                  title={isArchiveInterview ? 'What are credits?' : 'What are Proxi Credits?'}
                >
                  <span>
                    <b>{creditsBalance}</b> credits
                    {creditsEarned ? (
                      <span
                        style={{
                          display: 'inline-block',
                          color: 'var(--lp-pink)',
                          marginLeft: 5,
                          fontWeight: 700,
                          animation: 'lp-credits-pop 640ms cubic-bezier(.22,.61,.36,1) both',
                        }}
                      >
                        +{creditsEarned}
                      </span>
                    ) : null}
                  </span>
                </button>
              )}
            </div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <button
                type="button"
                onClick={toggleMuted}
                aria-label={isMuted ? 'Unmute' : 'Mute'}
                className="lp-icon-btn lp-topbar-icon"
              >
                {isMuted ? (
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 5 6 9H3v6h3l5 4z" />
                    <path d="M22 9 16 15" />
                    <path d="M16 9l6 6" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 5 6 9H3v6h3l5 4z" />
                    <path d="M16 8a5 5 0 0 1 0 8" />
                    <path d="M19 5a9 9 0 0 1 0 14" />
                  </svg>
                )}
              </button>
              <button
                type="button"
                onClick={() => setIsTopOpen(true)}
                aria-label="Open menu"
                className="lp-icon-btn lp-topbar-icon"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <circle cx="5" cy="12" r="1.6" />
                  <circle cx="12" cy="12" r="1.6" />
                  <circle cx="19" cy="12" r="1.6" />
                </svg>
              </button>
            </div>
            </div>
          </header>

          {/* Host avatar — pinned to its own grid row so its vertical
              position stays put as different-length questions cycle
              through. Renders an empty placeholder span when the question
              isn't visible so the row keeps reserving height. While a game
              is on, the game's card takes its place. */}
          {(() => {
            if (showGameCard && gameCard) return <GameCard view={gameCard} />;
            const hostAvatar = invitationData?.invitation?.inviterAvatar;
            const hostName = isArchiveInterview
              ? ARCHIVIST_NAME
              : invitationData?.invitation?.inviterName || 'Host';
            const useHostPhoto = !!hostAvatar && !hostAvatarFailed;
            // Active while Roxy is presenting the question; goes flat-grey once
            // it's the user's turn to respond (idle "tap to answer" or
            // USER_RECORDING).
            const isAsking = isRoxyActive;
            // Scales with viewport so the avatar reads proportionally next
            // to the orb (clamp(180px, 30vmin, 260px)). 12vmin matches a
            // small-but-legible chip on phones and grows on tablets/desktop.
            const avatarSize = 'clamp(56px, 12vmin, 88px)';
            if (!showText) {
              return (
                <span
                  style={{ display: 'inline-block', width: avatarSize, height: `calc(${avatarSize} + 6px)` }}
                  aria-hidden
                />
              );
            }
            return (
              <span
                aria-label={useHostPhoto ? hostName : 'Proxi'}
                style={{
                  position: 'relative',
                  width: avatarSize,
                  height: avatarSize,
                  marginBottom: 6,
                  display: 'inline-block',
                  flexShrink: 0,
                  ...(isAsking
                    ? { animation: 'lp-avatar-pulse 2.4s ease-in-out infinite' }
                    : { transition: 'transform 320ms ease', transform: 'scale(0.96)' }),
                }}
              >
                {/* Flat grey ring — visible when it's the user's turn. */}
                <span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    border: '1px solid rgba(40,50,40,0.22)',
                    opacity: isAsking ? 0 : 1,
                    transition: 'opacity 420ms ease',
                  }}
                />
                {/* Gradient ring — visible while the question is being asked. */}
                <span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    inset: -2,
                    borderRadius: '50%',
                    padding: 1,
                    background:
                      'conic-gradient(from 120deg, var(--lp-violet), var(--lp-pink), var(--lp-orange), var(--lp-amber), var(--lp-violet))',
                    WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                    WebkitMaskComposite: 'xor',
                    mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                    opacity: isAsking ? 0.95 : 0,
                    transition: 'opacity 420ms ease',
                    animation: isAsking ? 'lp-ring-rotate 5.5s linear infinite' : undefined,
                  }}
                />
                {/* Soft halo behind the disc — fades in while asking. */}
                <span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    inset: '-30%',
                    borderRadius: '50%',
                    background:
                      'radial-gradient(closest-side, rgba(236,72,153,0.18), transparent 70%)',
                    filter: 'blur(8px)',
                    zIndex: -1,
                    opacity: isAsking ? 1 : 0,
                    transition: 'opacity 420ms ease',
                  }}
                />
                {/* Inner photo / logo disc. */}
                <span
                  style={{
                    position: 'absolute',
                    inset: 2,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    display: 'grid',
                    placeItems: 'center',
                    background: useHostPhoto
                      ? 'var(--lp-paper-2)'
                      : 'radial-gradient(120% 120% at 30% 20%, rgba(255,255,255,0.95), rgba(255,255,255,0.55) 50%, rgba(238,239,232,0) 80%), linear-gradient(160deg, #faf9f3 0%, #ecede4 100%)',
                    boxShadow: useHostPhoto
                      ? 'inset 0 1px 0 rgba(255,255,255,0.6)'
                      : 'inset 0 1px 0 rgba(255,255,255,0.8), inset 0 -8px 14px rgba(40,50,40,0.06)',
                  }}
                >
                  <img
                    src={useHostPhoto ? hostAvatar : PROXI_BRAIN_LOGO}
                    alt={useHostPhoto ? hostName : 'Proxi'}
                    onError={() => { if (useHostPhoto) setHostAvatarFailed(true); }}
                    style={
                      useHostPhoto
                        ? {
                            // Absolute-fill instead of percentage sizing: on
                            // iOS Safari the percentage-sized grid-item photo
                            // could come up short and leave a paper-coloured
                            // band at the top of the disc.
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'center',
                            display: 'block',
                          }
                        : { width: '72%', height: '72%', opacity: 0.92 }
                    }
                  />
                </span>
              </span>
            );
          })()}

          {/* Question + game answers + photo-hint wrapper. Takes the 1fr grid
              row; stacks them in a column flex so the answers and the hint
              sit just below the question text. */}
          <div
            ref={setQuestionRow}
            style={{
              width: '100%',
              minHeight: 0,
              maxHeight: '100%',
              // Fill the row rather than size to the text, so the room the
              // message is fitted to is the row's, not the message's own.
              alignSelf: 'stretch',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
          {questionMedia ? (
            <QuestionMedia
              key={`${questionMedia.sourceType}:${questionMedia.sourceId}`}
              media={questionMedia}
              result={currentMessage?.mediaIdResult ?? null}
              hold={isRoxyActive || isUserTurn}
            />
          ) : null}
          <section
            key={messageText}
            data-question-box="true"
            style={{
              width: '100%',
              maxWidth: 820,
              display: 'flex',
              // NOT alignItems: 'center'. A centered flex child that is taller
              // than its container overflows both ways, and the half above the
              // top edge can't be reached. The paragraph centers itself with
              // auto margins instead, which collapse to zero if it overflows.
              alignItems: 'flex-start',
              justifyContent: 'center',
              textAlign: 'center',
              padding: '0 8px',
              opacity: showText ? 1 : 0,
              transition: 'opacity 400ms ease',
              // minHeight: 0 lets the flex item shrink below its content height
              // (the default min-content would defeat the parent row's
              // minmax(0, 1fr)).
              maxHeight: '100%',
              minHeight: 0,
            }}
            aria-live="polite"
          >
            {showText && (
              <RevealText
                text={messageText}
                progress={playbackProgress}
                fontSize={questionFontSize}
                minFontSize={QUESTION_MIN_PX}
                room={questionRoom}
                className="lp-serif"
                style={{
                  fontWeight: 300,
                  lineHeight: 1.18,
                  letterSpacing: '-0.018em',
                  color: 'var(--lp-ink)',
                  margin: 'auto 0',
                  textWrap: 'balance',
                }}
              />
            )}
          </section>
          {showGameAnswers && gameCard && (
            <GameAnswers
              view={gameCard}
              disabled={actionsDisabled || isUserTurn}
              ended={isEndChat}
              onReply={(text) => void handleGameReply(text)}
              onAddPhoto={categoryId ? () => void handleOpenAttachments() : undefined}
              onCreditsInfo={() => setShowCreditsInfo(true)}
            />
          )}
          {/* Always reserve space for the photo hint when the user has no
              avatar and we're inside the interview — otherwise its appearance
              on the IDLE branch reflows the question/orb columns each time
              Roxy stops speaking. Visibility is toggled instead so the slot
              keeps its height across all states. */}
          {showPhotoHint && (() => {
              const isTapToAnswer =
                !isStartDisabled &&
                !isUserTurn &&
                status !== 'ROXY_TYPING' &&
                status !== 'ROXY_SPEAKING';
              const hintLabel = checklistActive
                ? 'Complete Media Upload Requests'
                : 'Upload your photo to be featured';
              return (
                <button
                  type="button"
                  onClick={() =>
                    checklistActive
                      ? setShowMediaChecklist(true)
                      : setShowAttachmentModal(true)
                  }
                  className="lp-eyebrow-text"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '2px 8px',
                    border: 0,
                    background: 'transparent',
                    color: 'var(--lp-pink)',
                    fontSize: 9,
                    letterSpacing: '0.12em',
                    cursor: isTapToAnswer ? 'pointer' : 'default',
                    visibility: isTapToAnswer ? 'visible' : 'hidden',
                    pointerEvents: isTapToAnswer ? 'auto' : 'none',
                    animation: isTapToAnswer
                      ? 'lp-pulse 2.6s ease-in-out infinite'
                      : 'none',
                  }}
                  aria-hidden={!isTapToAnswer}
                  tabIndex={isTapToAnswer ? 0 : -1}
                  aria-label={hintLabel}
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="10"
                    height="10"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
                    <circle cx="12" cy="13" r="3" />
                  </svg>
                  <span>{hintLabel}</span>
                </button>
              );
            })()}
          </div>

          {/* Orb */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            {(() => {
              // The orb has two visual modes:
              //  - "live": cream paper, conic-gradient ring, drop-shadow,
              //    animated glyph. Used when the user can act or when Roxy
              //    is actively typing/listening.
              //  - "muted": flat greyscale + thin neutral border, no ring
              //    animation, no shadow, ink-soft glyph. Used when the orb
              //    is disabled because Roxy is mid-utterance — signals "wait,
              //    not your turn yet".
              const isMuted =
                !isEndChat && !isUserTurn && (status === 'ROXY_SPEAKING' || isStartDisabled);
              return (
                <button
                  type="button"
                  onClick={async () => {
                    if (await wakeIfNeeded()) return;
                    // Mid-recording the orb's only job is "stop" no matter the
                    // mode; otherwise it launches whichever answer flow the
                    // mode toggle has selected.
                    if (replyMode === 'video' && !isUserTurn) {
                      setShowVideoModal(true);
                      return;
                    }
                    toggleRecording();
                  }}
                  disabled={(disabled && !isUserTurn) || isEndChat || isStartDisabled}
                  aria-label={
                    isUserTurn
                      ? 'Stop recording'
                      : replyMode === 'video'
                        ? 'Record a video answer'
                        : 'Start talking'
                  }
                  style={{
                    position: 'relative',
                    width: 'clamp(180px, 30vmin, 260px)',
                    height: 'clamp(180px, 30vmin, 260px)',
                    border: 0,
                    background: 'transparent',
                    padding: 0,
                    cursor: 'pointer',
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    transition: 'transform 200ms ease, filter 320ms ease',
                  }}
                >
                  {/* Both ring layers are rendered at all times so the swap
                      between muted (flat neutral border) and live (conic
                      gradient + animation) crossfades via opacity instead of
                      snapping when the orb unmutes for "Tap to answer". */}
                  <span
                    aria-hidden
                    style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '50%',
                      border: '1px solid rgba(40,50,40,0.18)',
                      opacity: isMuted ? 1 : 0,
                      transition: 'opacity 420ms ease',
                    }}
                  />
                  <span
                    aria-hidden
                    style={{
                      position: 'absolute',
                      inset: -4,
                      borderRadius: '50%',
                      padding: 2,
                      background:
                        'conic-gradient(from 120deg, var(--lp-violet), var(--lp-pink), var(--lp-orange), var(--lp-amber), var(--lp-violet))',
                      WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                      WebkitMaskComposite: 'xor',
                      mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                      opacity: isMuted
                        ? 0
                        : isUserTurn
                          ? 1
                          : isRoxyActive
                            ? 0.85
                            : 0.6,
                      filter: 'saturate(0.95)',
                      transition: 'opacity 420ms ease',
                      animation: isMuted
                        ? undefined
                        : isUserTurn
                          ? 'lp-ring-rotate 4.5s linear infinite, lp-ring-breathe 2.6s ease-in-out infinite'
                          : status === 'ROXY_TYPING'
                            ? 'lp-ring-rotate 5.5s linear infinite reverse'
                            : undefined,
                    }}
                  />
                  {/* Soft drop shadow halo — fades in alongside the live ring. */}
                  <span
                    aria-hidden
                    style={{
                      position: 'absolute',
                      inset: '6% 6% -8% 6%',
                      borderRadius: '50%',
                      background:
                        'radial-gradient(closest-side, rgba(40,50,40,0.18), transparent 70%)',
                      filter: 'blur(14px)',
                      zIndex: -1,
                      opacity: isMuted ? 0 : 1,
                      transition: 'opacity 420ms ease',
                    }}
                  />
                  <span
                    style={{
                      position: 'relative',
                      zIndex: 1,
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      // Live cream-paper gradient lives on the base layer at
                      // all times — the flat muted fill is overlaid on top
                      // and faded in/out so the gradient/glow can crossfade
                      // smoothly (background-image isn't CSS-transitionable).
                      background:
                        'radial-gradient(140% 140% at 28% 18%, rgba(255,255,255,0.85), rgba(250,247,240,0.55) 45%, rgba(238,235,228,0) 78%), ' +
                        'radial-gradient(120% 120% at 80% 90%, rgba(236,72,153,0.10), rgba(139,92,246,0.06) 50%, transparent 78%), ' +
                        'linear-gradient(160deg, #faf9f3 0%, #ecebe1 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: isMuted
                        ? // Transparent twins of the live shadows so box-shadow
                          // can interpolate smoothly between states (browsers
                          // only animate same-shape shadow lists).
                          'inset 0 1px 0 rgba(255,255,255,0), ' +
                          'inset 0 -22px 32px rgba(40,50,40,0), ' +
                          'inset 0 0 32px rgba(236,72,153,0), ' +
                          '0 12px 30px -8px rgba(40,50,40,0)'
                        : 'inset 0 1px 0 rgba(255,255,255,0.85), ' +
                          'inset 0 -22px 32px rgba(40,50,40,0.06), ' +
                          'inset 0 0 32px rgba(236,72,153,0.06), ' +
                          '0 12px 30px -8px rgba(40,50,40,0.14)',
                      color: isMuted ? 'var(--lp-ink-mute)' : 'var(--lp-ink)',
                      transition: 'box-shadow 420ms ease, color 320ms ease',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Flat muted overlay — sits above the gradient and the
                        inset shadows, fades in when muted. */}
                    <span
                      aria-hidden
                      style={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '50%',
                        background: '#ecebe4',
                        opacity: isMuted ? 1 : 0,
                        transition: 'opacity 420ms ease',
                        pointerEvents: 'none',
                      }}
                    />
                    <span
                      style={{
                        position: 'relative',
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {isEndChat ? (
                        <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                      ) : isUserTurn ? (
                        <ListeningBars getLevel={getMicLevel} />
                      ) : status === 'ROXY_TYPING' ? (
                        <ThinkingDots />
                      ) : status === 'ROXY_SPEAKING' ? (
                        <svg viewBox="0 0 24 24" width="52" height="52" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M6 8.5a6.5 6.5 0 1 1 13 0c0 6-6 6-6 10a3.5 3.5 0 1 1-7 0" />
                          <path d="M15 8.5a2.5 2.5 0 0 0-5 0v1a2 2 0 1 1 0 4" />
                        </svg>
                      ) : replyMode === 'video' ? (
                        <AnimatedCamera active={!isMuted} />
                      ) : (
                        <AnimatedMic active={!isMuted} />
                      )}
                    </span>
                  </span>
                </button>
              );
            })()}
            <div
              className="lp-eyebrow-text"
              style={{
                // Lock the line box at the size of the largest cue so swapping
                // between states (or to an empty string during ROXY_SPEAKING)
                // doesn't reflow neighbouring elements. The listening cue is
                // the longest — drop it a notch and force a single line so it
                // never wraps under the orb on narrow viewports.
                fontSize: isUserTurn ? 11 : 13,
                lineHeight: '18px',
                minHeight: 18,
                whiteSpace: 'nowrap',
                letterSpacing: isUserTurn ? '0.08em' : undefined,
                color:
                  isUserTurn ? '#b8127b'
                  : status === 'ROXY_TYPING' ? '#6d28d9'
                  : 'var(--lp-ink-mute)',
              }}
            >
              {isEndChat
                ? 'Interview complete'
                : isStartDisabled
                  ? 'Preparing session…'
                  : isUserTurn
                    ? "I'm listening — tap when you're done"
                    : status === 'ROXY_TYPING'
                      ? (
                        <span key={thinkingPhrase} className="lp-thinking-phrase">
                          {thinkingPhrase}
                        </span>
                      )
                      : status === 'ROXY_SPEAKING'
                        ? ' '
                        : replyMode === 'video'
                          ? 'Video response'
                          : 'Tap to talk'}
            </div>
            {/* Answer-mode toggle — pre-selects what a tap on the orb does.
                Only rendered when this device can actually capture video;
                faded out (but still occupying its row so nothing reflows)
                while an audio recording is live, because mid-recording the
                orb's only job is "stop". */}
            {videoSupported && !isEndChat && (
              <div
                className="lp-mode-toggle"
                role="radiogroup"
                aria-label="Answer mode"
                style={{
                  opacity: isUserTurn ? 0 : 1,
                  pointerEvents: isUserTurn ? 'none' : 'auto',
                  transition: 'opacity 320ms ease',
                }}
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={replyMode === 'audio'}
                  className="lp-mode-toggle-opt"
                  onClick={() => changeReplyMode('audio')}
                >
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="3" width="6" height="12" rx="3" />
                    <path d="M5 11a7 7 0 0 0 14 0" />
                    <path d="M12 18v3" />
                  </svg>
                  <span>Voice</span>
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={replyMode === 'video'}
                  className="lp-mode-toggle-opt"
                  onClick={() => changeReplyMode('video')}
                >
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m16 10 6-3.5v11L16 14" />
                    <rect x="2" y="6" width="14" height="12" rx="2" />
                  </svg>
                  <span>Video</span>
                </button>
              </div>
            )}
          </div>

          {/* Actions */}
          {isEndChat ? (
            (() => {
              // Logged-in non-anonymous members return to their interview list;
              // anonymous one-shot participants get pointed at Proxi Studio.
              const isLoggedInMember =
                !!currentUser?._id && !!authUser && !authUser.isAnonymous && !isLinkSession;
              return isLoggedInMember ? (
                <button
                  type="button"
                  onClick={() => { window.location.href = '/'; }}
                  className="lp-btn lp-btn-primary"
                  style={{ minWidth: 240 }}
                >
                  Back to interviews
                </button>
              ) : (
                <a
                  href={
                    isArchiveInterview
                      ? 'https://www.proxiaistudio.com/archive'
                      : 'https://www.proxiaistudio.com'
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="lp-btn lp-btn-primary"
                  style={{ minWidth: 240, textDecoration: 'none' }}
                >
                  {isArchiveInterview ? 'Go to The Archive' : 'Go to Proxi Studio'}
                </a>
              );
            })()
          ) : (
            <div className="lp-action-bar">
              <button
                type="button"
                onClick={handleTextReply}
                disabled={actionsDisabled}
                className="lp-btn"
                style={{ background: 'transparent', border: '1px solid transparent', boxShadow: 'none' }}
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z" />
                </svg>
                <span>Type</span>
              </button>
              <button
                type="button"
                onClick={handleOpenAttachments}
                disabled={actionsDisabled || !categoryId}
                className="lp-btn"
                style={{ background: 'transparent', border: '1px solid transparent', boxShadow: 'none', position: 'relative' }}
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
                <span>Attach</span>
                {attachments.length > 0 && (
                  <span
                    aria-hidden
                    style={{
                      position: 'absolute',
                      top: 2, right: 4,
                      minWidth: 16, height: 16,
                      padding: '0 4px',
                      borderRadius: 999,
                      background: 'linear-gradient(135deg, var(--lp-pink), var(--lp-orange))',
                      color: '#fff',
                      fontSize: 9, fontWeight: 700,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {attachments.length}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={handleSkip}
                disabled={actionsDisabled || isRoxyActive}
                className="lp-btn"
                style={{ background: 'transparent', border: '1px solid transparent', boxShadow: 'none' }}
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 4v16l9-8z" />
                  <line x1="19" y1="5" x2="19" y2="19" />
                </svg>
                <span>Skip</span>
              </button>
            </div>
          )}
        </main>
      </div>

      {/* Legacy partials reskinned via body.lp-active CSS. */}
      <LightReplyModal
        isOpened={bottomSheetOpened}
        onClose={closeBottomSheet}
        onSubmit={submitFormResponse}
        onOpenAttachments={categoryId ? () => setShowAttachmentModal(true) : undefined}
        attachmentCount={attachments.length}
        attachmentsDisabled={!categoryId}
      />
      <TopModal
        isOpened={isTopOpen}
        setIsOpen={setIsTopOpen}
        resetChat={resetChat}
        setIsAwake={setIsAwake}
        showPopover={() => setIsPopoverOpen(true)}
      />
      <ActionsPopover isOpened={isPopoverOpen} onClose={() => setIsPopoverOpen(false)} />

      <WakeOverlay
        loading={loading || isStartDisabled || isInitializing}
        starting={isInitializing}
        inProp={!isAwake && !showSignInModal}
        wake={initTalk}
        contributorName={
          // Prefer the invitation's fullName — that comes from the URL's
          // invitation, so it always matches THIS link's intended recipient.
          // currentUser?.firstName can lag/leak across invitations during
          // the cold-load sign-in dance (Firebase persists the previous
          // anon user, CURRENT_USER fetches with that token before
          // signInGuestContributor re-binds to the new invite's predefined
          // user), and using it first causes the wake screen to flash the
          // correct name then change to the wrong one.
          invitationData?.invitation?.fullName ||
          currentUser?.personalData?.firstName ||
          'You'
        }
        adminName={interviewerName}
        adminAvatar={invitationData?.invitation?.inviterAvatar || undefined}
        seriesName={invitationData?.invitation?.seriesName || checkInLabel}
        circleName={invitationData?.invitation?.circleName}
        game={invitationData?.invitation?.game || checkIn?.game || undefined}
        currentLanguage={currentLanguage}
        onLanguageChange={handleLanguageChange}
        onBackToInterviews={
          currentUser?._id && authUser && !authUser.isAnonymous && !isLinkSession
            ? () => { window.location.href = '/'; }
            : undefined
        }
        onQuickUpload={
          isSelfCreatedTip && categoryId
            ? () => setShowQuickUploadModal(true)
            : undefined
        }
        // Gated on the tip snapshot so checklist-seeded attachments on
        // regular invited interviews never flip the card into its
        // "files sent" state.
        quickUploadedCount={isSelfCreatedTip ? attachments.length : 0}
      />

      <RegistrationModal
        open={showRegistration}
        onClose={() => setShowRegistration(false)}
        onRegistered={(result) => {
          setShowRegistration(false);
          if (invitationData?.invitation?.type === 'GLOBAL_LINK') {
            const categoryFromBackend = result?.category?._id || result?.category;
            if (categoryFromBackend) {
              setCategoryId(categoryFromBackend);
              originalInitTalk(categoryFromBackend);
              return;
            }
            setIsAwake(true);
            initTalk();
          } else {
            setShowConfirmation(true);
          }
        }}
        adminName={invitationData?.invitation?.inviterName}
        circleName={invitationData?.invitation?.circleName || 'Circle'}
        invitationEmail={invitationData?.invitation?.email}
        invitationToken={token ?? undefined}
        invitationType={invitationData?.invitation?.type}
        proxiCastName={invitationData?.document?.name}
      />

      <GlobalLinkConfirmationModal
        open={showGlobalLinkConfirmation}
        onContinue={async () => {
          setShowGlobalLinkConfirmation(false);
          if (currentUser && currentUser.personalData?.email && !authUser?.isAnonymous) {
            if (!isArchiveInterview && !hasStartedContributorOnboarding(currentUser)) {
              setShowContributorOnboarding(true);
              return;
            }
            try {
              const { data } = await acceptGlobalLinkInvitation({ variables: { input: { token } } });
              const categoryFromBackend = data?.acceptGlobalLinkInvitation?.category?._id;
              // Don't setIsAwake(true) here — let initTalk run the preload gate
              // in _createSequence so the WakeOverlay spinner stays up across the
              // fetch+preload window instead of flashing the interview screen.
              if (categoryFromBackend) {
                setCategoryId(categoryFromBackend);
                await originalInitTalk(categoryFromBackend);
              } else {
                initTalk();
              }
            } catch {
              initTalk();
            }
          } else {
            setShowSignInModal(true);
          }
        }}
        onLogout={() => {
          setShowGlobalLinkConfirmation(false);
          logout().then(() => setShowSignInModal(true));
        }}
        onSignIn={() => {
          setShowGlobalLinkConfirmation(false);
          setShowSignInModal(true);
        }}
        userEmail={
          currentUser && !authUser?.isAnonymous ? currentUser?.personalData?.email : undefined
        }
        proxiCastName={invitationData?.document?.name}
        adminName={invitationData?.invitation?.inviterName}
        showSignInOption={!currentUser || authUser?.isAnonymous}
      />

      {/* End-of-interview confirmation modal disabled — on END_CHAT the user
          stays on the main stage (closing message, "+N" credits chip, and the
          "Interview complete" orb) instead of having it covered by a modal.
      <ConfirmationModal
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        adminName={invitationData?.invitation?.inviterName}
        circleName={invitationData?.invitation?.circleName || 'Circle'}
        allowClose={currentMessage?.userResponseType === 'END_CHAT'}
        creditsEarned={creditsEarned}
      /> */}

      <CreditsInfoModal
        open={showCreditsInfo}
        onClose={() => setShowCreditsInfo(false)}
        balance={creditsBalance}
        isArchive={isArchiveInterview}
      />

      <SignInModal
        open={showSignInModal}
        onClose={() => setShowSignInModal(false)}
        invitationEmail={
          invitationData?.invitation?.type !== 'GLOBAL_LINK'
            ? invitationData?.invitation?.email
            : undefined
        }
        invitationToken={token ?? undefined}
        invitationType={invitationData?.invitation?.type}
        proxiCastName={invitationData?.document?.name}
        adminName={invitationData?.invitation?.inviterName}
        defaultPhone={inviteSignUpPhone ?? undefined}
        inviteLinkKey={inviteSignUpPhone !== null ? linkKey ?? undefined : undefined}
        isArchive={isArchiveInterview}
        onSignInSuccess={async (result?: any, backgroundWork?: Promise<any>) => {
          setIsAuthenticatedForInterview(true);
          setShowSignInModal(false);
          if (backgroundWork) pendingBackgroundWork.current = backgroundWork;
          const needsOnboarding = backgroundWork
            ? true
            : !hasStartedContributorOnboarding(currentUser || result);

          if (
            needsOnboarding &&
            !isArchiveInterview &&
            invitationData?.invitation?.type !== 'GUEST_CONTRIBUTOR'
          ) {
            setPendingSignInResult(result);
            setShowContributorOnboarding(true);
            return;
          }
          if (backgroundWork) {
            const bgResult = await backgroundWork;
            pendingBackgroundWork.current = null;
            if (invitationData?.invitation?.type === 'GLOBAL_LINK') {
              const categoryFromBackend = bgResult?.category?._id;
              if (categoryFromBackend) {
                setCategoryId(categoryFromBackend);
                setIsAwake(true);
                await originalInitTalk(categoryFromBackend);
              } else {
                setIsAwake(true);
                initTalk();
              }
            }
          } else if (invitationData?.invitation?.type === 'GLOBAL_LINK') {
            const categoryFromBackend = result?.category?._id;
            if (categoryFromBackend) {
              setCategoryId(categoryFromBackend);
              setIsAwake(true);
              await originalInitTalk(categoryFromBackend);
            } else {
              setIsAwake(true);
              initTalk();
            }
          }
        }}
      />

      <ContributorOnboarding
        open={showContributorOnboarding}
        onComplete={async () => {
          setShowContributorOnboarding(false);
          if (pendingBackgroundWork.current) {
            try {
              const bgResult = await pendingBackgroundWork.current;
              pendingBackgroundWork.current = null;
              if (invitationData?.invitation?.type === 'GLOBAL_LINK') {
                const categoryFromBackend = bgResult?.category?._id;
                if (categoryFromBackend) {
                  setCategoryId(categoryFromBackend);
                  setIsAwake(true);
                  await originalInitTalk(categoryFromBackend);
                } else {
                  setIsAwake(true);
                  initTalk();
                }
              }
            } catch {
              pendingBackgroundWork.current = null;
            }
          } else if (invitationData?.invitation?.type === 'GLOBAL_LINK') {
            if (pendingSignInResult?.category?._id) {
              const categoryFromBackend = pendingSignInResult.category._id;
              setCategoryId(categoryFromBackend);
              setIsAwake(true);
              await originalInitTalk(categoryFromBackend);
            } else {
              try {
                const { data } = await acceptGlobalLinkInvitation({ variables: { input: { token } } });
                const categoryFromBackend = data?.acceptGlobalLinkInvitation?.category?._id;
                if (categoryFromBackend) {
                  setCategoryId(categoryFromBackend);
                  setIsAwake(true);
                  await originalInitTalk(categoryFromBackend);
                } else {
                  setIsAwake(true);
                  initTalk();
                }
              } catch {
                setIsAwake(true);
                initTalk();
              }
            }
          }
          setPendingSignInResult(null);
        }}
        userName={currentUser?.personalData?.firstName || undefined}
        currentUser={currentUser}
      />

      {categoryId && (
        <AttachmentModal
          isOpen={showAttachmentModal}
          onClose={() => setShowAttachmentModal(false)}
          categoryId={categoryId}
          existingAttachments={attachments}
          onAttachmentsChange={setAttachments}
          // Show the dedicated avatar uploader (AI-vetted, saves personalData.
          // avatar) whenever the recipient has no profile photo — this is what
          // the flashing "Upload your photo to be featured" hint promises.
          // Without it the hint opens a plain attachment picker that never sets
          // the avatar. Mirrors the non-Light TrainScreen. Close on success so
          // the now-satisfied hint (gated on !avatar) disappears. Not while a
          // game asks for a photo: that photo is the answer, not an avatar.
          showProfilePhotoUpload={!currentUser?.personalData?.avatar && !currentGame?.wantsPhoto}
          onProfilePhotoUploaded={() => setShowAttachmentModal(false)}
          // Wire "Upload as Answer" to actually advance the interview. Without
          // this, the modal would upload the file silently and never submit
          // a reply — leaving the user staring at the same question with no
          // visible acknowledgement. The upload's URL rides along as the
          // reply's attachment: the API analyzes a photo for the interviewer
          // and tags the upload to this question, which is what shows it on
          // the answer in the Archive.
          onUseAsAnswer={(text, attachmentUrl) => {
            submitFormResponse({ text, attachment: attachmentUrl });
          }}
        />
      )}

      {/* News-tip quick upload: same pipeline, no interview. Separate
          instance so its open state and stripped-down chrome never collide
          with the in-interview paperclip modal above. */}
      {categoryId && isSelfCreatedTip && (
        <AttachmentModal
          isOpen={showQuickUploadModal}
          onClose={() => setShowQuickUploadModal(false)}
          categoryId={categoryId}
          existingAttachments={attachments}
          onAttachmentsChange={setAttachments}
          quickUploadMode
        />
      )}

      <VideoReplyModal
        isOpened={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        // The sheet stays open across answers: stopping a recording submits it
        // immediately (no review step), the camera preview never drops, and the
        // next question is overlaid on it — so the contributor never bounces
        // back to the orb screen mid-interview. The END_CHAT effect above
        // closes it when the interview finishes.
        onSubmit={(result) => {
          void submitVideoResponse(result);
        }}
        questionText={showText ? messageText : ''}
        isThinking={status === 'ROXY_TYPING'}
        thinkingPhrase={thinkingPhrase}
        isSpeaking={status === 'ROXY_SPEAKING'}
        playbackProgress={playbackProgress}
        onOpenAttachments={categoryId ? () => setShowAttachmentModal(true) : undefined}
        attachmentCount={attachments.length}
        onSkip={() => void handleSkip()}
        gameChoices={gameCard?.choices}
        gameActions={gameCard?.actions}
        onGameReply={gameCard ? (text) => void handleGameReply(text) : undefined}
        wantsPhoto={gameCard?.wantsPhoto}
      />

      {categoryId && checklistActive && (
        <MediaChecklistModal
          isOpen={showMediaChecklist}
          onClose={() => setShowMediaChecklist(false)}
          categoryId={categoryId}
          mediaRequests={mediaRequests}
          attachments={attachments}
          onAttachmentsChange={setAttachments}
          hasAvatar={!!currentUser?.personalData?.avatar}
          onProfilePhotoUploaded={() => {
            apolloClient.refetchQueries({ include: [CURRENT_USER] });
          }}
        />
      )}
    </>
  );
}

/** Live waveform indicator while the user is speaking. Each bar's scaleY is
 *  driven from the recorder's RMS level (smoothed) so the bars actually move
 *  with the user's voice — empty silence reads as a low resting line, not a
 *  fake busy animation. Drives DOM via refs to avoid per-frame React state. */
function ListeningBars({ getLevel }: { getLevel: () => number }) {
  // Heights are the maximum heights each bar can take. The per-bar phase
  // offset makes the centre bar lead and the edges lag so the row reads as a
  // travelling waveform rather than five identical meters.
  const maxHeights = [18, 28, 34, 24, 20];
  const phases = [-0.20, -0.40, 0.00, -0.50, -0.15];
  const barRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const smoothedRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    let mounted = true;
    const tick = (now: number) => {
      if (!mounted) return;
      const raw = Math.max(0, Math.min(1, getLevel() || 0));
      // Asymmetric one-pole smoothing: rise quickly so the meter responds to
      // sudden syllables, decay slowly so the bars don't strobe between words.
      const target = raw;
      const prev = smoothedRef.current;
      const alpha = target > prev ? 0.45 : 0.12;
      const next = prev + (target - prev) * alpha;
      smoothedRef.current = next;

      // Resting motion when the user hasn't spoken yet — keeps the meter
      // looking alive at silence without faking volume. Sub-2px swing.
      const idleSwing = 0.08 + 0.03 * Math.sin(now / 320);

      for (let i = 0; i < barRefs.current.length; i += 1) {
        const el = barRefs.current[i];
        if (!el) continue;
        // Phase the per-bar level so the row ripples, then clamp to [0.2, 1].
        const phased = Math.max(0, Math.min(1, next + phases[i] * next * 0.6));
        const scale = Math.max(0.2, Math.min(1.05, phased + idleSwing));
        el.style.transform = `scaleY(${scale.toFixed(3)})`;
        el.style.opacity = (0.55 + 0.45 * Math.min(1, next * 1.4)).toFixed(3);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      mounted = false;
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [getLevel]);

  return (
    <span
      aria-hidden
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        width: 70,
        height: 36,
      }}
    >
      {maxHeights.map((h, i) => (
        <span
          key={i}
          ref={(el) => { barRefs.current[i] = el; }}
          style={{
            width: 5,
            height: h,
            background: 'linear-gradient(180deg, #b8127b 0%, #d44a3b 100%)',
            borderRadius: 3,
            transformOrigin: '50% 50%',
            // scaleY/opacity get written every frame from the rAF loop above.
            // Short CSS transition smooths sub-frame jitter without lagging
            // visibly behind the actual voice envelope.
            transition: 'transform 70ms linear, opacity 120ms linear',
          }}
        />
      ))}
    </span>
  );
}

/** Mic glyph. When `active` (idle "tap to answer" state), runs a multicolored
 *  animated stroke so the orb invites a tap. When inactive it falls back to a
 *  flat ink stroke. Recording feedback is handled by [[ListeningBars]]. */
function AnimatedMic({ active }: { active: boolean }) {
  const gradId = 'lp-mic-grad';
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: active ? 'lp-mic-pulse 1.5s ease-in-out infinite' : undefined,
      }}
    >
      <svg viewBox="0 0 24 24" width="56" height="56" fill="none" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="var(--lp-violet)">
              {active && (
                <animate attributeName="stop-color" values="#8b5cf6; #ec4899; #f97316; #f59e0b; #8b5cf6" dur="2.6s" repeatCount="indefinite" />
              )}
            </stop>
            <stop offset="50%" stopColor="var(--lp-pink)">
              {active && (
                <animate attributeName="stop-color" values="#ec4899; #f97316; #f59e0b; #8b5cf6; #ec4899" dur="2.6s" repeatCount="indefinite" />
              )}
            </stop>
            <stop offset="100%" stopColor="var(--lp-orange)">
              {active && (
                <animate attributeName="stop-color" values="#f97316; #f59e0b; #8b5cf6; #ec4899; #f97316" dur="2.6s" repeatCount="indefinite" />
              )}
            </stop>
          </linearGradient>
        </defs>
        <g stroke={active ? `url(#${gradId})` : 'currentColor'}>
          <rect x="9" y="3" width="6" height="12" rx="3" />
          <path d="M5 11a7 7 0 0 0 14 0" />
          <path d="M12 18v3" />
        </g>
      </svg>
      <style>{`
        @keyframes lp-mic-pulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.06); }
        }
      `}</style>
    </span>
  );
}

/** Video-mode twin of AnimatedMic — same pulse and cycling gradient stroke so
 *  swapping the orb between answer modes reads as a costume change, not a
 *  different component. */
function AnimatedCamera({ active }: { active: boolean }) {
  const gradId = 'lp-cam-grad';
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: active ? 'lp-mic-pulse 1.5s ease-in-out infinite' : undefined,
      }}
    >
      <svg viewBox="0 0 24 24" width="56" height="56" fill="none" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="var(--lp-violet)">
              {active && (
                <animate attributeName="stop-color" values="#8b5cf6; #ec4899; #f97316; #f59e0b; #8b5cf6" dur="2.6s" repeatCount="indefinite" />
              )}
            </stop>
            <stop offset="50%" stopColor="var(--lp-pink)">
              {active && (
                <animate attributeName="stop-color" values="#ec4899; #f97316; #f59e0b; #8b5cf6; #ec4899" dur="2.6s" repeatCount="indefinite" />
              )}
            </stop>
            <stop offset="100%" stopColor="var(--lp-orange)">
              {active && (
                <animate attributeName="stop-color" values="#f97316; #f59e0b; #8b5cf6; #ec4899; #f97316" dur="2.6s" repeatCount="indefinite" />
              )}
            </stop>
          </linearGradient>
        </defs>
        <g stroke={active ? `url(#${gradId})` : 'currentColor'}>
          <path d="m16 10 6-3.5v11L16 14" />
          <rect x="2" y="6" width="14" height="12" rx="2" />
        </g>
      </svg>
      {/* AnimatedMic declares the same keyframes, but it isn't mounted while
          the orb is in video mode — declare them here too so the pulse works
          whichever glyph renders first. */}
      <style>{`
        @keyframes lp-mic-pulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.06); }
        }
      `}</style>
    </span>
  );
}

function ThinkingDots() {
  return (
    <span aria-hidden style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: 36 }}>
      {[-0.24, -0.12, 0].map((delay, i) => (
        <span
          key={i}
          style={{
            width: 9,
            height: 9,
            borderRadius: 99,
            background: 'linear-gradient(135deg, var(--lp-violet), var(--lp-pink))',
            animation: 'lp-dots-bounce 1.1s ease-in-out infinite',
            animationDelay: `${delay}s`,
          }}
        />
      ))}
      <style>{`
        @keyframes lp-dots-bounce {
          0%, 80%, 100% { transform: translateY(0) scale(0.9); opacity: 0.55; }
          40%           { transform: translateY(-9px) scale(1.0); opacity: 1; }
        }
      `}</style>
    </span>
  );
}
